import { MentionComposer } from "./MentionComposer";
import { mentionText } from "../shared/mentions";
import { toRichText, type TLShapeId } from "@tldraw/tlschema";
import { SheetPreview } from "./SheetPreview";
import { useRef, useState } from "react";
import { paperOpacity } from "../shared/trace-paper";
import { ReviewControls, ThreadActions } from "./ReviewControls";
import { AgentReplyActions, CommentAuthor } from "./AgentReply";
import { useAgentThreads } from "./agent-threads";
import { orderedContents, reorderContent } from "./workspace-documents";
import { nativeDocument } from "../shared/document-session";
import {
  ArrowUpRight,
  ChevronRight,
  Plus,
  FileText,
  Check,
  Lock,
  Unlock,
} from "lucide-react";

import type { WorkspaceModel } from "./useWorkspace";
import { sheetFor } from "./shapes";
import { displayObjectName } from "./object-name";
import type { BoardDocument, Trace } from "../shared/schema";
export function Sidebar({
  model,
  onNewSheet,
  onOpenDocument,
  onCreateDocument,
}: {
  model: WorkspaceModel;
  onNewSheet: () => void;
  onOpenDocument: (id: TLShapeId) => void;
  onCreateDocument: () => void;
}) {
  const {
    editor,
    room,
    user,
    sheets,
    active,
    threads,
    comments,
    tab,
    pending,
    setPending,
    message,
    setMessage,
    reply,
    setReply,
    threadId,
    setThreadId,
    canEdit,
    canComment,
    openFilePicker,
    choose,
    focusSheet,
    focusThread,
    addComment,
    addReply,
    textOf,
  } = model;
  const agents = useAgentThreads();
  const [sheetQuery, setSheetQuery] = useState("");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const renameCancelled = useRef(false);
  const [dragged, setDragged] = useState<string | null>(null);
  const contents = orderedContents(editor);
  const visibleContents = contents.filter((sheet) =>
    `${sheet.props.name} ${sheet.type === "sheet" ? sheet.props.revision : ""}`
      .toLocaleLowerCase()
      .includes(sheetQuery.trim().toLocaleLowerCase()),
  );
  const moveContent = (fromId: string, toId: string) => {
    const from = contents.findIndex((sheet) => sheet.id === fromId);
    const to = contents.findIndex((sheet) => sheet.id === toId);
    if (!canEdit || from < 0 || to < 0 || from === to) return;
    const ordered = [...contents];
    ordered.splice(to, 0, ...ordered.splice(from, 1));
    editor.markHistoryStoppingPoint("Reorder contents");
    editor.updateShapes(
      ordered.map((sheet, index) => ({
        id: sheet.id,
        type: sheet.type,
        meta: { ...sheet.meta, order: index },
      })),
    );
  };
  const commitRename = (id: string) => {
    const sheet = contents.find((item) => item.id === id);
    if (
      sheet &&
      canEdit &&
      !sheet.isLocked &&
      renameDraft.trim() &&
      renameDraft.trim() !== sheet.props.name
    ) {
      editor.markHistoryStoppingPoint("Rename content");
      editor.updateShape({
        id: sheet.id,
        type: sheet.type,
        props: {
          name: renameDraft
            .trim()
            .slice(0, sheet.type === "document" ? 120 : 100),
        },
      });
    }
    setRenaming(null);
  };
  const traces = model.shapes.filter(
    (shape): shape is Trace =>
      shape.type === "trace" && shape.parentId.startsWith("page:"),
  );
  return (
    <>
      <aside className="left-panel">
        {tab === "drawings" ? (
          <div className="panel-content">
            <div className="section-label">
              ON THIS BOARD{" "}
              <span>
                {(contents.length + traces.length).toString().padStart(2, "0")}
              </span>
            </div>
            {!!contents.length && (
              <label className="sheet-search">
                Search contents
                <input
                  type="search"
                  aria-label="Search contents"
                  value={sheetQuery}
                  placeholder="Name or revision"
                  onChange={(event) => setSheetQuery(event.target.value)}
                />
              </label>
            )}
            {sheets.length > 1 && (
              <div className="sheet-nav-actions">
                <button
                  onClick={() => {
                    editor.selectNone();
                    const bounds = editor.getCurrentPageBounds();
                    if (bounds)
                      editor.zoomToBounds(bounds, {
                        inset:
                          editor.getViewportScreenBounds().w > 760 ? 300 : 80,
                        animation: { duration: 200 },
                      });
                  }}
                >
                  All sheets
                </button>
                <button
                  disabled={!active || sheets.indexOf(active) === 0}
                  onClick={() => focusSheet(sheets[sheets.indexOf(active) - 1])}
                >
                  Previous
                </button>
                <button
                  disabled={
                    !active || sheets.indexOf(active) === sheets.length - 1
                  }
                  onClick={() => focusSheet(sheets[sheets.indexOf(active) + 1])}
                >
                  Next
                </button>
              </div>
            )}
            {visibleContents.map((s) => {
              const i = contents.findIndex((sheet) => sheet.id === s.id);
              return (
                <div
                  key={s.id}
                  className="sheet-nav-item"
                  draggable={canEdit && !renaming}
                  onDragStart={(event) => {
                    setDragged(s.id);
                    event.dataTransfer.setData("text/plain", s.id);
                    event.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(event) => {
                    if (canEdit && dragged && dragged !== s.id)
                      event.preventDefault();
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (dragged) moveContent(dragged, s.id);
                    setDragged(null);
                  }}
                  onDragEnd={() => setDragged(null)}
                >
                  <button
                    className={`drawing-row ${active?.id === s.id ? "chosen" : ""}`}
                    onClick={() => {
                      if (s.type === "sheet") focusSheet(s);
                      else {
                        editor.select(s.id);
                        const bounds = editor.getShapePageBounds(s);
                        if (bounds) editor.zoomToBounds(bounds, { inset: 140 });
                      }
                    }}
                  >
                    {s.type === "sheet" ? (
                      <SheetPreview editor={editor} sheet={s} />
                    ) : (
                      <FileText size={18} />
                    )}
                    <span>
                      <strong>{s.props.name}</strong>
                      <small>
                        {s.type === "document"
                          ? "Document"
                          : s.props.source
                            ? "Imported plan"
                            : `${s.props.paper} · ${
                                s.props.w >= s.props.h
                                  ? "Landscape"
                                  : "Portrait"
                              }`}{" "}
                        · {String(i + 1).padStart(2, "0")}
                        {s.type === "sheet" &&
                        s.props.revision &&
                        s.props.revision !== "01"
                          ? ` · Rev ${s.props.revision}`
                          : ""}
                        {s.meta.iterationDate
                          ? ` · ${String(s.meta.iterationDate)}`
                          : ""}
                      </small>
                    </span>
                    <ChevronRight size={14} />
                  </button>
                  {canEdit && !s.isLocked && renaming === s.id ? (
                    <input
                      className="sheet-inline-name"
                      aria-label={`Rename ${s.props.name}`}
                      autoFocus
                      value={renameDraft}
                      maxLength={s.type === "document" ? 120 : 100}
                      onChange={(event) => setRenameDraft(event.target.value)}
                      onBlur={() => {
                        if (!renameCancelled.current) commitRename(s.id);
                        renameCancelled.current = false;
                      }}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        if (event.key === "Enter") {
                          event.preventDefault();
                          commitRename(s.id);
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          renameCancelled.current = true;
                          setRenaming(null);
                        }
                      }}
                    />
                  ) : canEdit && !s.isLocked ? (
                    <button
                      className="sheet-inline-rename"
                      aria-label={`Rename ${s.props.name}`}
                      onClick={() => {
                        renameCancelled.current = false;
                        setRenameDraft(s.props.name);
                        setRenaming(s.id);
                      }}
                    >
                      Rename
                    </button>
                  ) : null}
                  {canEdit && (
                    <button
                      className="sheet-lock"
                      aria-label={`${s.isLocked ? "Unlock" : "Lock"} ${s.props.name}`}
                      onClick={() => editor.toggleLock([s.id])}
                    >
                      {s.isLocked ? <Unlock size={14} /> : <Lock size={14} />}{" "}
                      {s.isLocked ? "Unlock" : "Lock"}
                    </button>
                  )}
                  {s.type === "document" && (
                    <button
                      className="content-document-open"
                      onClick={() => onOpenDocument(s.id)}
                    >
                      Open document
                    </button>
                  )}
                  {s.type === "sheet" &&
                    model.shapes
                      .filter(
                        (child): child is BoardDocument =>
                          child.type === "document" &&
                          child.parentId === s.id &&
                          !nativeDocument(child),
                      )
                      .map((child) => (
                        <button
                          className="content-imported-row"
                          key={child.id}
                          onClick={() => {
                            editor.select(child.id);
                            const bounds = editor.getShapePageBounds(child);
                            if (bounds)
                              editor.zoomToBounds(bounds, { inset: 140 });
                          }}
                        >
                          <FileText size={14} />
                          {child.props.name}
                        </button>
                      ))}
                  {canEdit && contents.length > 1 && (
                    <div className="sheet-order">
                      <button
                        aria-label={`Move ${s.props.name} earlier`}
                        disabled={!i}
                        onClick={() => reorderContent(editor, s.id, -1)}
                      >
                        ↑
                      </button>
                      <button
                        aria-label={`Move ${s.props.name} later`}
                        disabled={i === contents.length - 1}
                        onClick={() => reorderContent(editor, s.id, 1)}
                      >
                        ↓
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {!!sheetQuery && !visibleContents.length && (
              <small>No contents match this search.</small>
            )}
            {!!traces.length && (
              <>
                <div className="section-label">TRACING SHEETS</div>
                {traces.map((t, i) => (
                  <div key={t.id}>
                    <button
                      className="drawing-row"
                      onClick={() => {
                        editor.select(t.id);
                        editor.zoomToBounds(editor.getShapePageBounds(t)!, {
                          inset: 200,
                        });
                        model.setPanel(null);
                      }}
                    >
                      <SheetPreview editor={editor} sheet={t} />
                      <span>
                        <strong>{t.props.name}</strong>
                        <small>
                          Tracing ·{" "}
                          {String(contents.length + i + 1).padStart(2, "0")}
                          {t.props.hidden
                            ? " · Hidden"
                            : paperOpacity(t) === 0
                              ? " · Transparent"
                              : ""}
                        </small>
                      </span>
                      <ChevronRight size={14} />
                    </button>
                    {canEdit && (
                      <div className="sheet-order trace-visibility">
                        <button
                          aria-label={`${t.props.hidden ? "Show" : "Hide"} ${t.props.name}`}
                          onClick={() =>
                            editor.updateShape({
                              id: t.id,
                              type: "trace",
                              props: { hidden: !t.props.hidden },
                            })
                          }
                        >
                          {t.props.hidden ? "Show" : "Hide"}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
            {canEdit && (
              <>
                <button className="upload-button" onClick={onNewSheet}>
                  <Plus size={17} />
                  New blank sheet
                </button>
                <button className="upload-button" onClick={onCreateDocument}>
                  <Plus size={17} /> New document
                </button>
                <button className="upload-button" onClick={openFilePicker}>
                  <Plus size={17} />
                  Upload a plan or image
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="panel-content comments-list">
            {canComment && (
              <div className="comment-start-actions">
                <button className="upload-button" onClick={() => choose("pin")}>
                  <Plus size={16} />
                  Point comment
                </button>
                <button
                  className="upload-button"
                  onClick={() => choose("comment-area")}
                >
                  Area
                </button>
              </div>
            )}
            {threads.map((t, i) => {
              const target =
                  t.anchor.type === "shape"
                    ? editor.getShape(t.anchor.shapeId)
                    : undefined,
                sheet = sheetFor(editor, target),
                object =
                  t.anchor.type === "region"
                    ? "Marked area"
                    : t.anchor.type === "point"
                      ? "Canvas point"
                      : target
                        ? target.type === "sheet"
                          ? "Sheet"
                          : target.type === "image"
                            ? displayObjectName(
                                target.meta.name || target.props.altText,
                              )
                            : target.type === "text"
                              ? "Text"
                              : target.type === "trace"
                                ? "Tracing sheet"
                                : "Drawing object"
                        : "Target unavailable";
              return (
                <div
                  key={t.id}
                  className={`comment-card ${t.resolved ? "resolved" : ""}`}
                >
                  <button
                    className="comment-heading"
                    onClick={() => {
                      focusThread(t);
                    }}
                  >
                    <span className="pin-number">{i + 1}</span>
                    <strong>
                      {t.resolved
                        ? t.anchor.type === "region"
                          ? "Resolved area discussion"
                          : "Resolved"
                        : t.anchor.type === "region"
                          ? "Area discussion"
                          : "Open discussion"}
                    </strong>
                    <small>Rev {String(t.meta.revision || "01")}</small>
                  </button>
                  <small className="comment-target-context">
                    {sheet?.props.name || "Board"}
                    {target?.type === "sheet" ? "" : ` · ${object}`}
                  </small>
                  {comments
                    .filter((c) => c.threadId === t.id)
                    .sort((a, b) => a.createdAt - b.createdAt)
                    .map((c) => (
                      <div className="comment-message" key={c.id}>
                        <strong>
                          <CommentAuthor comment={c} userId={user.id} />
                        </strong>
                        <p>{textOf(c.body)}</p>
                        <AgentReplyActions
                          comment={c}
                          editor={editor}
                          canEdit={canEdit}
                        />
                      </div>
                    ))}
                  {t.anchor.type === "shape" &&
                    !editor.getShape(t.anchor.shapeId) && (
                      <p className="hint">
                        Drawing was removed. Its discussion and actions remain
                        available.
                      </p>
                    )}
                  {threadId === t.id && room.platform === "cloudflare" && (
                    <ThreadActions
                      board={room.id}
                      thread={t.id}
                      canChange={canComment}
                      people={model.people}
                    />
                  )}
                  {canComment && (
                    <>
                      <button
                        className="text-button"
                        onClick={() =>
                          editor.store.put([
                            {
                              ...t,
                              resolved: t.resolved
                                ? null
                                : { at: Date.now(), by: user.id },
                            },
                          ])
                        }
                      >
                        <Check size={13} />
                        {t.resolved ? "Reopen" : "Resolve"}
                      </button>
                      {threadId === t.id ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            addReply(t);
                          }}
                        >
                          <MentionComposer
                            label="Reply to comment"
                            placeholder="Reply or type @ to mention…"
                            value={reply}
                            onChange={setReply}
                            people={model.people}
                            error={model.peopleError}
                            onRetry={model.retryPeople}
                            enabled={
                              room.platform === "cloudflare" || agents.enabled
                            }
                            agent={agents.enabled}
                          />

                          {room.platform === "cloudflare" && (
                            <ReviewControls
                              people={model.people}
                              value={model.review}
                              onChange={model.setReview}
                            />
                          )}
                          <button
                            aria-label="Post reply"
                            disabled={
                              !mentionText(reply).trim() ||
                              mentionText(reply).length > 3000
                            }
                          >
                            <ArrowUpRight size={16} />
                          </button>
                        </form>
                      ) : (
                        <button
                          className="text-button"
                          onClick={() => {
                            setThreadId(t.id);
                            setReply(toRichText(""));
                          }}
                        >
                          Reply
                        </button>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {!threads.length && !pending && (
              <p className="empty-comments">
                Keep the conversation
                <br />
                close to the drawing.
              </p>
            )}
          </div>
        )}
        <div className="panel-footer">
          <span className="user-avatar">{user.name[0].toUpperCase()}</span>
          <div>
            <strong>{user.name}</strong>
            <small>
              {room.role} ·{" "}
              {room.platform === "cloudflare"
                ? "private project"
                : "local workspace"}
            </small>
          </div>
        </div>
      </aside>
    </>
  );
}
